import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { IconButton } from '@7f-ui/components/button/icon-button';
import { useToast } from '@7f-ui/components/toast/toast-provider';
import type { ProductType } from '@data-access/graphql/schema.types';
import { trackAddToWishlist, trackOpenChangeWishlist, trackRemoveFromWishlist } from '@feat/feat-tracking/wishlist';
import { HeartStraight } from '@phosphor-icons/react';
import { baseUrl } from '@utils/utils-config';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { toastDuration } from '../constants';
import { useWishlistMutation } from '../hooks/use-wishlist-mutation';
import { useWishlistName } from '../hooks/use-wishlist-name';
import { useWishlists } from '../hooks/use-wishlists';
import { ChangeWishlistModal } from './change-wishlist-modal';
import { CreateWishlistModal } from './create-wishlist-modal';
import { getWishlistTrackingItemId } from './wishlist-tracking-item-id';
export type ToggleWishlistItemProps = {
  productId: string;
  publicProductId: number;
  productType: ProductType;
  productName: string;
  locationName?: string;
  image?: string;
  stars?: number | null | undefined;
  duration?: number | null;
  iconOnly?: boolean;
  origin: 'list' | 'pdp';
  onAdd?: () => void | Promise<unknown>;
};
export function WishlistToggle(t0) {
  const $ = _c(54);
  const {
    productId,
    publicProductId,
    productType,
    productName,
    locationName,
    image,
    stars,
    duration,
    iconOnly,
    origin,
    onAdd
  } = t0;
  const toast = useToast();
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [lastUsedWishlist, setLastUsedWishlist] = useLocalStorage("lastUsedWishlist", null);
  const {
    getWishlistName
  } = useWishlistName();
  const {
    wishlists,
    isLoading: wishlistsLoad
  } = useWishlists();
  const {
    addItemToWishlist,
    removeItemFromWishlist,
    isLoading: mutationsLoad
  } = useWishlistMutation();
  const isLoading = wishlistsLoad || mutationsLoad;
  let t1;
  if ($[0] !== productId || $[1] !== productType || $[2] !== wishlists) {
    t1 = wishlists?.flatMap(_temp).find(item => item.productId === productId && item.productType === productType)?.id;
    $[0] = productId;
    $[1] = productType;
    $[2] = wishlists;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const wishlistItemId = t1;
  const itemIsOnWishlist = Boolean(wishlistItemId);
  let t2;
  if ($[4] !== addItemToWishlist || $[5] !== duration || $[6] !== getWishlistName || $[7] !== image || $[8] !== lastUsedWishlist || $[9] !== locationName || $[10] !== onAdd || $[11] !== origin || $[12] !== productId || $[13] !== productName || $[14] !== productType || $[15] !== publicProductId || $[16] !== setLastUsedWishlist || $[17] !== stars || $[18] !== toast) {
    t2 = async () => {
      const createWishlistItemInput = {
        productId,
        publicProductId,
        productType,
        productName,
        locationName: locationName ?? "Unbekannter Ort",
        image: image ?? baseUrl("/images/7f-logo-fallback.svg"),
        stars,
        duration
      };
      const {
        addItemToWishlist: result
      } = await addItemToWishlist({
        wishlistId: lastUsedWishlist,
        item: createWishlistItemInput
      });
      setLastUsedWishlist(result.id);
      trackAddToWishlist({
        origin,
        itemName: productName,
        itemId: getWishlistTrackingItemId(publicProductId, productType),
        type: productType
      });
      const listName = getWishlistName(result.name);
      toast({
        type: "success",
        message: <>{"Gespeichert in "}<strong>{listName}</strong></>,
        actionLabel: "\xC4NDERN",
        actionOnClick: () => {
          setShowChangeModal(true);
          trackOpenChangeWishlist();
        }
      }, {
        toastOptions: {
          duration: toastDuration
        }
      });
      await onAdd?.();
    };
    $[4] = addItemToWishlist;
    $[5] = duration;
    $[6] = getWishlistName;
    $[7] = image;
    $[8] = lastUsedWishlist;
    $[9] = locationName;
    $[10] = onAdd;
    $[11] = origin;
    $[12] = productId;
    $[13] = productName;
    $[14] = productType;
    $[15] = publicProductId;
    $[16] = setLastUsedWishlist;
    $[17] = stars;
    $[18] = toast;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  const handleAdd = t2;
  let t3;
  if ($[20] !== getWishlistName || $[21] !== origin || $[22] !== productId || $[23] !== productName || $[24] !== productType || $[25] !== publicProductId || $[26] !== removeItemFromWishlist || $[27] !== toast) {
    t3 = async () => {
      if (!productId) {
        return;
      }
      const {
        removeItemFromWishlist: result_0
      } = await removeItemFromWishlist({
        productId,
        productType
      });
      trackRemoveFromWishlist({
        origin,
        itemName: productName,
        itemId: getWishlistTrackingItemId(publicProductId, productType),
        type: productType
      });
      const listName_0 = getWishlistName(result_0.name);
      toast({
        type: "success",
        message: <>{"Entfernt aus "}<strong>{listName_0}</strong></>
      });
    };
    $[20] = getWishlistName;
    $[21] = origin;
    $[22] = productId;
    $[23] = productName;
    $[24] = productType;
    $[25] = publicProductId;
    $[26] = removeItemFromWishlist;
    $[27] = toast;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  const handleRemove = t3;
  let t4;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = shopTestId("WishlistToggleCTA");
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  const t5 = itemIsOnWishlist ? handleRemove : handleAdd;
  const t6 = iconOnly ? "blank" : "withBorder";
  const t7 = iconOnly ? "icon" : "medium";
  let t8;
  if ($[30] !== iconOnly || $[31] !== itemIsOnWishlist) {
    t8 = () => <HeartStraight size={24} className={cx({
      "cursor-pointer text-primary/75": iconOnly && !itemIsOnWishlist,
      "text-primary": !iconOnly && !itemIsOnWishlist,
      "text-primary-red": itemIsOnWishlist
    })} weight={itemIsOnWishlist || iconOnly ? "fill" : "bold"} stroke={iconOnly ? "white" : ""} strokeWidth={iconOnly ? 20 : 0} />;
    $[30] = iconOnly;
    $[31] = itemIsOnWishlist;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const t9 = itemIsOnWishlist ? "Produkt von Wunschliste entfernen" : "Produkt zur Wunschliste hinzuf\xFCgen";
  let t10;
  if ($[33] !== iconOnly || $[34] !== isLoading || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t10 = <IconButton data-testid={t4} onClick={t5} variant={t6} iconOnly={iconOnly} size={t7} disabled={isLoading} Icon={t8} aria-label={t9} />;
    $[33] = iconOnly;
    $[34] = isLoading;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== setLastUsedWishlist || $[42] !== showChangeModal || $[43] !== wishlistItemId || $[44] !== wishlists) {
    t11 = showChangeModal && <ChangeWishlistModal isOpen={showChangeModal} onToggleChangeModal={open => setShowChangeModal(open)} onOpenCreateModal={() => setShowCreateModal(true)} wishlists={wishlists} wishlistItemId={wishlistItemId} onChangeLastUsedWishlist={id => setLastUsedWishlist(id)} />;
    $[41] = setLastUsedWishlist;
    $[42] = showChangeModal;
    $[43] = wishlistItemId;
    $[44] = wishlists;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== setLastUsedWishlist || $[47] !== showCreateModal || $[48] !== wishlistItemId) {
    t12 = showCreateModal && <CreateWishlistModal isOpen={showCreateModal} wishlistItemId={wishlistItemId} onOpenChangeModal={() => setShowChangeModal(true)} onCloseCreateModal={() => setShowCreateModal(false)} onChangeLastUsedWishlist={id_0 => setLastUsedWishlist(id_0)} />;
    $[46] = setLastUsedWishlist;
    $[47] = showCreateModal;
    $[48] = wishlistItemId;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== t10 || $[51] !== t11 || $[52] !== t12) {
    t13 = <>{t10}{t11}{t12}</>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  return t13;
}
function _temp(w) {
  return w.items;
}